import {
  CalendarDaysIcon,
  CheckCircleIcon,
  NoSymbolIcon,
  PauseCircleIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { Button } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { formatPlanPauseDate } from 'src/functions/planPause.functions';
import PauseFinalizeConfirmation from './PauseFinalizeConfirmation.component';
import RetentionOfferShell from '../RetentionOfferModal/RetentionOfferShell.component';
import { formatRetentionOfferAmount } from '../RetentionOfferModal/RetentionOfferModal.functions';
import usePauseOffer from './PauseOfferModal.hook';
import type { tPauseOfferModalProps } from './PauseOfferModal.types';

const STAYS = ['data', 'crm', 'team', 'channels', 'incoming'] as const;
const STOPS = [
  'sending',
  'campaigns',
  'automation',
  'scheduled',
  'addons',
] as const;

/**
 * The pause offer, on the chrome every offer step shares.
 *
 * What stays and what stops are both itemised rather than summarised, and sit side by
 * side because the pair is the point. U21 names the five entries on each side, and the
 * restore amount is the ONLY notice the customer gets before the full charge comes back
 * (KD9) — v1 sends no email — so neither list collapses into a single sentence no matter
 * how much quieter that would look.
 */
export default function PauseOfferModal(props: tPauseOfferModalProps) {
  const {
    isOpen,
    offer,
    onAcceptOffer,
    onFinalizeSubscription,
    onBack,
    onClose,
    isLoadingOfferAcceptance,
    isLoadingFinalization,
  } = props;
  const { t, i18n } = useTranslation('settings/SubscriptionManager');
  // Declining sits one mis-click from accepting, on a screen whose subject is keeping
  // the workspace intact, and it cannot be undone — so it asks first.
  const [isConfirmingFinalize, setIsConfirmingFinalize] = useState(false);
  const { selectedMonths, selectedOption, setSelectedMonths } = usePauseOffer(
    offer.pause,
  );
  const monthlyAmount = formatRetentionOfferAmount({
    amount: offer.pause.monthlyAmount,
    currency: offer.pause.currency,
    locale: i18n.language,
  });
  const restoreAmount = formatRetentionOfferAmount({
    amount: offer.pause.restoreAmount,
    currency: offer.pause.currency,
    locale: i18n.language,
  });
  const isLoading = isLoadingOfferAcceptance || isLoadingFinalization;
  const startsAt = formatPlanPauseDate({
    instant: selectedOption.startsAt,
    locale: i18n.language,
  });
  const endsAt = formatPlanPauseDate({
    instant: selectedOption.endsAt,
    locale: i18n.language,
  });

  if (isConfirmingFinalize) {
    return (
      <PauseFinalizeConfirmation
        isOpen={isOpen}
        onBack={() => setIsConfirmingFinalize(false)}
        onConfirm={onFinalizeSubscription}
        isLoading={isLoadingFinalization}
      />
    );
  }

  return (
    <RetentionOfferShell
      isOpen={isOpen}
      heading={t('cancelSubscription.pauseOffer.heading')}
      intro={t('cancelSubscription.pauseOffer.intro')}
      cardIcon={PauseCircleIcon}
      cardTitle={t('cancelSubscription.pauseOffer.cardHeading')}
      onBack={onBack}
      onDecline={(event) => {
        // Nothing is finalized here: the shell's decline is a form submit, so it is
        // stopped and turned into the confirmation step.
        event.preventDefault();
        setIsConfirmingFinalize(true);
      }}
      onAccept={() => onAcceptOffer(selectedMonths)}
      acceptLabel={t('cancelSubscription.pauseOffer.acceptButton')}
      declineLabel={t('cancelSubscription.pauseOffer.declineButton')}
      onClose={onClose}
      isLoadingAccept={isLoadingOfferAcceptance}
      isLoadingDecline={isLoadingFinalization}
      testIdPrefix="pause-offer"
      cardFooter={
        <>
          <p className="text-base font-semibold text-neutral-800">
            {t('cancelSubscription.pauseOffer.restoreHeading')}
          </p>
          <p className="text-base text-neutral-700">
            {t('cancelSubscription.pauseOffer.restoreDescription', {
              endsAt,
              plan: offer.pause.restorePlanName,
              seats: offer.pause.restoreSeats,
              amount: restoreAmount ?? offer.pause.currency,
            })}
          </p>
          <p className="text-sm text-neutral-600">
            {t('cancelSubscription.pauseOffer.restoreNotice')}
          </p>
          {offer.pause.discountsRestartAtRestore && (
            <p
              className="text-sm font-medium text-neutral-700"
              data-testid="pause-discount-restart"
            >
              {t('cancelSubscription.pauseOffer.discountRestart')}
            </p>
          )}
        </>
      }
    >
      <div>
        <p className="text-sm text-neutral-600">
          {t('cancelSubscription.pauseOffer.monthlyPriceLabel')}
        </p>
        <p className="text-neutral-800" data-testid="pause-monthly-price">
          <span className="text-3xl font-bold">
            {monthlyAmount ?? offer.pause.currency}
          </span>
          <span className="text-base text-neutral-600">
            {' '}
            {t('cancelSubscription.pauseOffer.perMonth')}
          </span>
        </p>
      </div>

      <fieldset className="flex flex-col gap-2" disabled={isLoading}>
        <legend className="mb-2 text-base font-semibold text-neutral-800">
          {t('cancelSubscription.pauseOffer.monthSelectorLabel')}
        </legend>
        <div
          role="group"
          className="flex flex-wrap gap-2"
          aria-label={t('cancelSubscription.pauseOffer.monthSelectorAriaLabel')}
        >
          {offer.pause.monthOptions.map((option) => (
            <Button
              key={option.months}
              type="button"
              color={selectedMonths === option.months ? 'primary' : 'ghost'}
              className={`min-w-[104px] rounded-lg font-normal ${
                selectedMonths === option.months
                  ? ''
                  : 'border border-neutral-300 text-neutral-700'
              }`}
              aria-pressed={selectedMonths === option.months}
              onClick={() => setSelectedMonths(option.months)}
              disabled={isLoading}
              data-testid={`pause-month-${option.months}`}
            >
              {t('cancelSubscription.pauseOffer.monthOption', {
                count: option.months,
              })}
            </Button>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-col gap-1">
        <p className="flex items-start gap-2 text-base text-neutral-800">
          <CalendarDaysIcon
            aria-hidden="true"
            className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
          />
          {t('cancelSubscription.pauseOffer.dateRange', { startsAt, endsAt })}
        </p>
        <p className="pl-7 text-sm text-neutral-600">
          {t('cancelSubscription.pauseOffer.fullServiceUntil', { startsAt })}
        </p>
      </div>

      <div className="grid gap-x-8 gap-y-4 border-t border-neutral-200 pt-4 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <p className="text-base font-semibold text-neutral-800">
            {t('cancelSubscription.pauseOffer.staysHeading')}
          </p>
          <ul className="flex flex-col gap-2">
            {STAYS.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2 text-base text-neutral-700"
              >
                <CheckCircleIcon
                  aria-hidden="true"
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-success"
                />
                {t(`cancelSubscription.pauseOffer.stays.${item}`)}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-2 border-t border-neutral-200 pt-4 md:border-l md:border-t-0 md:pl-8 md:pt-0">
          <p className="text-base font-semibold text-neutral-800">
            {t('cancelSubscription.pauseOffer.stopsHeading')}
          </p>
          <ul className="flex flex-col gap-2">
            {STOPS.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2 text-base text-neutral-700"
              >
                <NoSymbolIcon
                  aria-hidden="true"
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-neutral-500"
                />
                {t(`cancelSubscription.pauseOffer.stops.${item}`)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </RetentionOfferShell>
  );
}
